import React, { useState, useEffect, useCallback, useMemo } from 'react';
import EventFormModal from './EventFormModal'; // Add this at the top
import moment from 'moment';
import ConfirmModal from './components/ConfirmModal';
import ErrorModal from './components/ErrorModal';
import MobileNavigation from './components/MobileNavigation';
import MobileFooter from './components/MobileFooter';
import SeriesModal from './components/SeriesModal';
import CustomDateRangeModal from './components/CustomDateRangeModal';
import ListView from './components/ListView';
import ThreeDayView from './components/ThreeDayView';
import {
  Calendar,
  momentLocalizer,
} from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { useRef, useLayoutEffect } from 'react';
import './App.css';
import { API } from './api/api';
import { formatHours, transformTimelogsToEvents, handlePeopleTimelogToEvent, transformLeaveToEvent } from './utils/events';
import { getSeriesCreationState, setSeriesCreationState, addCreatedEventToSeries } from './utils/seriesState';
import { createCalendarComponents } from './components/calendar/createCalendarComponents';
import { useContextMenus } from './hooks/useContextMenus';
import { useCalendarMetrics } from './hooks/useCalendarMetrics';
import { useAuthBootstrap } from './hooks/useAuthBootstrap';
import AuthenticatingModal from './components/AuthenticatingModal';
import PresenceView from './components/PresenceView';


const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

moment.locale('en', {
  week: {
    dow: 1, // Start week on Monday
  },
  monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
  longDateFormat: {
    LT: 'HH:mm',
    LTS: 'HH:mm:ss',
  }
});

const FORMATS = {
    timeGutterFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),

    eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,

    selectRangeFormat: ({ start, end }, culture, localizer) =>
      `${localizer.format(start, 'HH:mm', culture)} – ${localizer.format(end, 'HH:mm', culture)}`,

    agendaTimeFormat: (date, culture, localizer) =>
      localizer.format(date, 'HH:mm', culture),
  };

function App() {
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [currentView, setCurrentView] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const v = p.get('view');
    return ['week', 'day', 'work_week', 'month', 'agenda'].includes(v) ? v : 'week';
  });
  const [currentDate, setCurrentDate] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    const d = p.get('date');
    if (d) { const parsed = new Date(d); if (!isNaN(parsed)) return parsed; }
    return new Date();
  });
  const [error, setError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [slotInfo, setSlotInfo] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [eventPendingDelete, setEventPendingDelete] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalData, setErrorModalData] = useState({ title: '', message: '', variant: 'error' });
  const [portalName, setPortalName] = useState(null);
  const [featureFlags, setFeatureFlags] = useState({ enablePeople: false, enableLeave: false, enableOutlookSync: false, enableCRM: false, enableSeries: false, moduleType: 'general', enableStatsBar: false, enableRolle: false, isAdmin: false, employees: [], currentUserName: '' });
  const [preloadedAssignedProjects, setPreloadedAssignedProjects] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null); // null = own calendar
  const [showPeople, setShowPeople] = useState(() => new URLSearchParams(window.location.search).get('tab') === 'people');
  const [showProjects, setShowProjects] = useState(() => new URLSearchParams(window.location.search).get('tab') !== 'people' && new URLSearchParams(window.location.search).get('tab') !== 'leave');
  const [showLeave, setShowLeave] = useState(() => new URLSearchParams(window.location.search).get('tab') === 'leave');
  const [showOutlookSync, setShowOutlookSync] = useState(true);
  const [appView, setAppView] = useState(() => {
    const v = new URLSearchParams(window.location.search).get('appView');
    return ['calendar', 'presence'].includes(v) ? v : 'form';
  });
  const [standaloneKey, setStandaloneKey] = useState(0);
  const [calendarKey, setCalendarKey] = useState(0);
  const [standaloneHasChanges, setStandaloneHasChanges] = useState(false);
  const [standaloneTimerTab, setStandaloneTimerTab] = useState(null);
  const [pendingAppView, setPendingAppView] = useState(null);
  const [showStandaloneUnsavedModal, setShowStandaloneUnsavedModal] = useState(false);

  useEffect(() => {
    if (isFirstMountRef.current) { isFirstMountRef.current = false; return; }
    if (appView === 'calendar') {
      setShowLeave(false);
      setShowPeople(false);
      setShowProjects(true);
    }
  }, [appView]);

  // Sync view/date/tab/appView to URL so reload restores state
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('view', currentView);
    params.set('date', currentDate.toISOString().split('T')[0]);
    const tab = showLeave ? 'leave' : showPeople ? 'people' : 'projects';
    params.set('tab', tab);
    if (appView !== 'form') params.set('appView', appView);
    window.history.replaceState(null, '', `?${params.toString()}`);
  }, [currentView, currentDate, showProjects, showPeople, showLeave, appView]);

  // Persist selected employee ZUIDID in sessionStorage (not URL — keeps IDs out of browser history)
  useEffect(() => {
    try {
      if (selectedEmployee?.Zuid) {
        sessionStorage.setItem('selectedEmployeeZuid', String(selectedEmployee.Zuid));
      } else if (employeeRestoredRef.current) {
        // Only clear after restore has been attempted — prevents wiping the saved value on initial mount
        sessionStorage.removeItem('selectedEmployeeZuid');
      }
    } catch (e) {
      // iOS Safari private mode throws SecurityError on sessionStorage access
    }
  }, [selectedEmployee]);


  const standaloneStart = useMemo(() => {
    const now = new Date();
    const m = now.getMinutes();
    const r = Math.floor(m / 30) * 30;
    now.setMinutes(r, 0, 0);
    return now;
  }, [standaloneKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const standaloneEnd = useMemo(() => new Date(standaloneStart.getTime() + 60 * 60 * 1000), [standaloneStart]);


  const filteredEvents = useMemo(() => {
    if (featureFlags.enablePeople || featureFlags.enableLeave) {
      return events.filter(ev => {
        if (featureFlags.enableLeave) {
          if (showLeave) return !!ev.isHR || !!ev.isPeopleLeave;
          if (showPeople) return !!ev.isPeople;
          return !ev.isPeople && !ev.isHR && !ev.isPeopleLeave; // Projects: exclude People/HR/leave events
        }
        // enablePeople only (no enableLeave) — legacy behavior
        return (showPeople && !!ev.isPeople) || (showProjects && !ev.isPeople);
      });
    }
    if (featureFlags.enableOutlookSync) {
      return events.filter(ev => {
        // Identify Outlook sync events by their ID prefix or task name
        const isOutlookSync = ev.timecodeName === '(SYNC) Outlook Sync' || String(ev.id || ev.logId || '').startsWith('crm:') || String(ev.id || ev.logId || '').startsWith('projects:crm:');
        return showOutlookSync || !isOutlookSync;
      });
    }
    return events;
  }, [events, showPeople, showProjects, showLeave, showOutlookSync, featureFlags]);

  const stripPrefix = useCallback((id) => {
    if (typeof id !== 'string') return id;
    return id.replace(/^(people|projects):/, '');
  }, []);

  const getModalPositionStyle = useCallback((date, view) => {
    const day = moment(date).day();
    let positionStyle = { right: '20px' };
    if (view === 'week') {
      if (day === 0 || day >= 4) positionStyle = { left: '20px' };
    } else if (view === 'work_week') {
      if (day >= 3) positionStyle = { left: '20px' };
    }
    return {
      ...positionStyle,
      position: 'fixed',
      top: '80px',
      zIndex: 2600,
    };
  }, [currentDate]);

  const [modalPosition, setModalPosition] = useState(() => getModalPositionStyle(new Date(), currentView));
  const [isDragging, setIsDragging] = useState(false);

  // Show error in modal instead of taking over the page
  const showError = useCallback((title, message) => {
    setErrorModalData({ title, message, variant: 'error' });
    setShowErrorModal(true);
  }, []);

  const showSuccess = useCallback((title, message) => {
    setErrorModalData({ title, message, variant: 'success' });
    setShowErrorModal(true);
  }, []);

  const {
    eventToCopy,
    setEventToCopy,
    contextMenu,
    setContextMenu,
    contextMenuRef,
    slotContextMenu,
    setSlotContextMenu,
    slotContextMenuRef,
    handleRightClickEvent,
    handleRightClickSlot,
  } = useContextMenus();
  const [originalEventBeforeEdit, setOriginalEventBeforeEdit] = useState(null);
  const suppressRevertRef = useRef(false); // <-- add this
  const isFirstMountRef = useRef(true);
  const employeeRestoredRef = useRef(false);

  const [selectedSlotDate, setSelectedSlotDate] = useState(null);
  
  // Series Modal state
  const [showSeriesModal, setShowSeriesModal] = useState(false);
  const [seriesTemplateEvent, setSeriesTemplateEvent] = useState(null);
  
  // Series Delete state
  const [showSeriesDeleteConfirm, setShowSeriesDeleteConfirm] = useState(false);
  const [seriesPendingDelete, setSeriesPendingDelete] = useState({ seriesId: null, events: [] });
  const [isDeletingSeries, setIsDeletingSeries] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState({ current: 0, total: 0 });

  // 🛡️ Series Recovery state  
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const [isRecovering, setIsRecovering] = useState(false);

  // Custom Date Range state
  const [showCustomDateModal, setShowCustomDateModal] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null);
  const [customRangeData, setCustomRangeData] = useState(null);
  const [isLoadingCustomRange, setIsLoadingCustomRange] = useState(false);

  const [customRangeContext, setCustomRangeContext] = useState('projects'); // NEW

  const [billableAutoFill, setBillableAutoFill] = useState(false);

  // Outlook Sync Removal state
  const [showRemoveSyncsConfirm, setShowRemoveSyncsConfirm] = useState(false);
  const [outlookSyncsToRemove, setOutlookSyncsToRemove] = useState([]);
  const [showRemoveSyncsResult, setShowRemoveSyncsResult] = useState(false);



  // Handle Create Series functionality (from right-click)
  const handleCreateSeries = useCallback((templateEvent) => {
    setSeriesTemplateEvent(templateEvent);
    setShowSeriesModal(true);
    setContextMenu({ visible: false, x: 0, y: 0, event: null });
  }, []);

  // Handle Create Series from Header (without template)
  const handleCreateSeriesFromHeader = useCallback(() => {
    // Create a default template event for new series
    const defaultTemplate = {
      title: '',
      projectId: '',
      timecodeId: '',
      billingtype: 'Billable',
      projectName: '',
      timecodeName: '',
      approval_status: 'Pending',
      start: new Date(),
      end: new Date(),
    };
    setSeriesTemplateEvent(defaultTemplate);
    setShowSeriesModal(true);
  }, []);

  const handleRecoveryCleanup = useCallback(async () => {
    setIsRecovering(true);
    if (recoveryData?.createdEvents?.length) {
      for (const eventInfo of recoveryData.createdEvents) {
        try {
          await API.deleteTimelog({
            logId: stripPrefix(eventInfo.logId),
            projectId: eventInfo.projectId,
            timecodeId: eventInfo.timecodeId
          });
          setEvents(prev => prev.filter(ev => ev.logId !== eventInfo.logId));
        } catch (error) {
          console.error('Error cleaning up event:', eventInfo.logId, error);
        }
      }
      setSeriesCreationState(null);
    }
    setShowRecoveryModal(false);
    setRecoveryData(null);
    setIsRecovering(false);
  }, [recoveryData, stripPrefix]);

  const handleRecoveryDismiss = useCallback(() => {
    // Clear localStorage without cleanup - user chooses to keep partial series
    setSeriesCreationState(null);
    setShowRecoveryModal(false);
    setRecoveryData(null);
  }, []);

  // Handle Custom Date Range
  const handleCustomDateRangeClick = useCallback((context = 'projects') => {
    setCustomRangeContext(context);                     // NEW
    setShowCustomDateModal(true);
  }, []);

  

  const handleCustomDateRangeClose = useCallback(() => {
    setShowCustomDateModal(false);
  }, []);

  const handleClearCustomRange = useCallback(() => {
    setCustomDateRange(null);
    setCustomRangeData(null);
  }, []);

  // 🛡️ Recovery System - Check for incomplete series on app start
  useEffect(() => {
    const checkForIncompleteCreation = () => {
      const incompleteState = getSeriesCreationState();
      if (incompleteState) {
        const timeElapsed = Date.now() - incompleteState.startTime;
        const hoursElapsed = timeElapsed / (1000 * 60 * 60);
        
        console.log(`🔍 Found incomplete series creation: ${incompleteState.createdEvents.length}/${incompleteState.totalEvents} events created`);
        
        setRecoveryData({
          ...incompleteState,
          hoursElapsed: Math.round(hoursElapsed * 10) / 10
        });
        setShowRecoveryModal(true);
      }
    };

    // Check after a short delay to ensure app is loaded
    const timer = setTimeout(checkForIncompleteCreation, 1000);
    return () => clearTimeout(timer);
  }, []);

  // 🛡️ BeforeUnload Protection - Warn user about incomplete series
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const incompleteState = getSeriesCreationState();
      if (incompleteState) {
        e.preventDefault();
        e.returnValue = 'You are currently creating a series. If you leave this page, all already created events will be automatically deleted.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);


  // Helper function for long press on mobile
  const setupLongPress = useCallback((handler, contextData = null) => ({
    onTouchStart: (e) => {
      const touchTimer = setTimeout(() => {
        const syntheticEvent = {
          preventDefault: () => e.preventDefault(),
          clientX: e.touches[0].clientX,
          clientY: e.touches[0].clientY,
          target: e.target,
          currentTarget: e.currentTarget
        };
        handler(contextData || e, syntheticEvent);
      }, 500); // 500ms long press
      
      e.currentTarget._touchTimer = touchTimer;
    },
    onTouchEnd: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    },
    onTouchMove: (e) => {
      if (e.currentTarget._touchTimer) {
        clearTimeout(e.currentTarget._touchTimer);
        e.currentTarget._touchTimer = null;
      }
    }
  }), []);

  // Handle Delete Entire Series functionality
  const handleDeleteSeries = useCallback((seriesId) => {
    if (isDeletingSeries) return; // Don't allow new deletions while deleting
    
    const seriesEvents = events.filter(event => event.seriesId === seriesId);
    if (seriesEvents.length > 0) {
      setSeriesPendingDelete({ seriesId, events: seriesEvents });
      setShowSeriesDeleteConfirm(true);
      setContextMenu({ visible: false, x: 0, y: 0, event: null });
    }
  }, [events, isDeletingSeries]);

  // Confirm and execute series deletion
  const handleConfirmDeleteSeries = useCallback(async () => {
    setShowSeriesDeleteConfirm(false);
    setIsDeletingSeries(true);
    setDeleteProgress({ current: 0, total: seriesPendingDelete.events.length });
    
    try {
      let deletedCount = 0;
      for (const event of seriesPendingDelete.events) {
        if (event.logId && event.projectId && event.timecodeId) {
          await API.deleteTimelog({
            logId: stripPrefix(event.logId),
            projectId: event.projectId,
            timecodeId: event.timecodeId
          });
          deletedCount++;
          setDeleteProgress({ current: deletedCount, total: seriesPendingDelete.events.length });
        } else {
          deletedCount++;
          setDeleteProgress({ current: deletedCount, total: seriesPendingDelete.events.length });
        }
      }
      setEvents(prev => prev.filter(event => event.seriesId !== seriesPendingDelete.seriesId));
      setSeriesPendingDelete({ seriesId: null, events: [] });
      setIsDeletingSeries(false);
      setDeleteProgress({ current: 0, total: 0 });
    } catch (error) {
      console.error('Error deleting series:', error);
      setIsDeletingSeries(false);
      setDeleteProgress({ current: 0, total: 0 });
      showError('Delete Failed', 'Failed to delete some events in the series. Please try again.');
    }
  }, [seriesPendingDelete, showError]);

  // Handle batch creation of series events
  const handleBatchCreateSeries = useCallback(async (eventsToCreate, setProgress) => {
    const seriesId = eventsToCreate[0]?.seriesId;
    if (!seriesId) throw new Error('Series ID is required');

    const seriesState = {
      seriesId,
      totalEvents: eventsToCreate.length,
      createdEvents: [],
      startTime: Date.now(),
      templateData: {
        projectId: eventsToCreate[0]?.projectId,
        timecodeId: eventsToCreate[0]?.timecodeId,
        title: eventsToCreate[0]?.title,
        billingtype: eventsToCreate[0]?.billingtype,
      }
    };
    setSeriesCreationState(seriesState);

    try {
      for (let i = 0; i < eventsToCreate.length; i++) {
        const eventData = eventsToCreate[i];
        setProgress({ current: i, total: eventsToCreate.length });

        const data = await API.createTimelog({
          start: eventData.start,
          end: eventData.end,
          projectId: eventData.projectId,
          timecodeId: eventData.timecodeId,
          notes: eventData.title,
          bill_status: eventData.billingtype,
          date: eventData.start.toISOString().split('T')[0],
        });

        if (data.success) {
          const newEventWithId = {
            title: eventData.title,
            start: eventData.start,
            end: eventData.end,
            projectId: eventData.projectId,
            timecodeId: eventData.timecodeId,
            projectName: eventData.projectName || '',
            timecodeName: eventData.timecodeName || '',
            billingtype: eventData.billingtype || '',
            approval_status: 'Pending',
            logId: `projects:${data.data.id}`,
            seriesId: eventData.seriesId,
          };
          setEvents((prev) => [...prev, newEventWithId]);
          addCreatedEventToSeries(data.data.id, eventData.projectId, eventData.timecodeId);
        } else {
          throw new Error('Failed to save event');
        }

        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setProgress({ current: eventsToCreate.length, total: eventsToCreate.length });
      setSeriesCreationState(null);
      if (authenticated) {
        API.fetchProjects().then((projectData) => {
          if (projectData.success) setProjects(projectData.data);
        }).catch(() => {});
      }
    } catch (error) {
      console.error('🚨 Error creating series:', error);
      const currentState = getSeriesCreationState();
      if (currentState?.createdEvents?.length) {
        for (const eventInfo of currentState.createdEvents) {
          try {
            await API.deleteTimelog({
              logId: stripPrefix(eventInfo.logId),
              projectId: eventInfo.projectId,
              timecodeId: eventInfo.timecodeId
            });
            setEvents(prev => prev.filter(ev => ev.logId !== eventInfo.logId));
          } catch (err) {
            console.error('Error rolling back event:', eventInfo.logId, err);
          }
        }
        setSeriesCreationState(null);
      }
      throw error;
    }
  }, [authenticated, stripPrefix]);

  const createMarkedPlaceholder = useCallback((start, end, allDay) => {
    const tempId = `temp-${Date.now()}`;
    const base = {
      title: '',
      start,
      end,
      logId: tempId,
      approval_status: 'Marked',
      projectId: '',
      timecodeId: '',
      billingtype: '',
      projectName: '',
      timecodeName: '',
    };
    return allDay === undefined ? base : { ...base, allDay };
  }, []);

  const [isRefreshing, setIsRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const empZuid  = selectedEmployee?.Zuid  || null;
      const empEmail = selectedEmployee?.email || null;
      const [projectsData, peopleData, leaveData, assignedData] = await Promise.all([
        API.refreshTimelogs(empZuid),
        featureFlags.enablePeople ? API.refreshPeopleTimelogs(empEmail).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        featureFlags.enableLeave ? API.fetchLeaveRequests(empZuid).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        featureFlags.filterProjectsByAssignment ? API.refreshAssignedProjects().catch(() => null) : Promise.resolve(null),
      ]);
      const allLogs = projectsData.data?.flatMap(group => group.timelogs || []) || [];
      const projectEvents = transformTimelogsToEvents({ timelogs: { date: allLogs } });
      const peopleLogs = peopleData.data?.flatMap(group => group.timelogs || []) || [];
      const peopleEvents = peopleLogs.map(log => handlePeopleTimelogToEvent(log)).filter(Boolean);
      const leaveItems = Array.isArray(leaveData.data) ? leaveData.data : [];
      const leaveEvents = leaveItems.flatMap(transformLeaveToEvent);
      setEvents([...projectEvents, ...peopleEvents, ...leaveEvents]);
      if (assignedData?.success) setPreloadedAssignedProjects(assignedData.data);
    } catch {
      // silently ignore
    } finally {
      setIsRefreshing(false);
    }
  }, [featureFlags.enablePeople, featureFlags.enableLeave, featureFlags.filterProjectsByAssignment, selectedEmployee]);

  const handleEmployeeChange = useCallback(async (employee) => {
    setSelectedEmployee(employee);
    setIsRefreshing(true);
    try {
      const empZuid  = employee?.Zuid  || null;
      const empEmail = employee?.email || null;
      const [projectsData, peopleData, leaveData] = await Promise.all([
        API.fetchProjectsTimelogs(empZuid),
        featureFlags.enablePeople ? API.fetchPeopleTimelogs(empEmail).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
        featureFlags.enableLeave  ? API.fetchLeaveRequests(empZuid).catch(() => ({ data: [] }))  : Promise.resolve({ data: [] }),
      ]);
      const allLogs = projectsData.data?.flatMap(group => group.timelogs || []) || [];
      const projectEvents = transformTimelogsToEvents({ timelogs: { date: allLogs } });
      const peopleLogs = peopleData.data?.flatMap(group => group.timelogs || []) || [];
      const peopleEvents = peopleLogs.map(log => handlePeopleTimelogToEvent(log)).filter(Boolean);
      const leaveItems = Array.isArray(leaveData.data) ? leaveData.data : [];
      const leaveEvents = leaveItems.flatMap(transformLeaveToEvent);
      setEvents([...projectEvents, ...peopleEvents, ...leaveEvents]);
    } catch {
      // silently ignore
    } finally {
      setIsRefreshing(false);
    }
  }, [featureFlags.enablePeople, featureFlags.enableLeave]);

  // Restore selected employee from sessionStorage once employees list is available after auth
  useEffect(() => {
    if (employeeRestoredRef.current) return;
    if (!featureFlags.employees?.length) return;
    let saved;
    try { saved = sessionStorage.getItem('selectedEmployeeZuid'); } catch (e) { return; }
    if (!saved) return;
    const match = featureFlags.employees.find(e => `${e.Zuid}` === saved);
    if (match) {
      employeeRestoredRef.current = true;
      handleEmployeeChange(match);
    }
  }, [featureFlags.employees, handleEmployeeChange]);

  // Pre-fetch assigned projects on load so the Kunde dropdown is ready before first modal open
  useEffect(() => {
    if (!authenticated || !featureFlags.filterProjectsByAssignment) return;
    API.refreshAssignedProjects()
      .then(res => { if (res.success) setPreloadedAssignedProjects(res.data); })
      .catch(() => {});
  }, [authenticated, featureFlags.filterProjectsByAssignment]);

  // Reset selected employee and calendar view/date when switching to calendar view
  const previousAppViewRef2 = useRef(null);
  useEffect(() => {
    if (previousAppViewRef2.current !== null && previousAppViewRef2.current !== 'calendar' && appView === 'calendar') {
      if (selectedEmployee) handleEmployeeChange(null);
      // Reset calendar to current week view
      setCurrentView('week');
      setCurrentDate(new Date());
    }
    previousAppViewRef2.current = appView;
  }, [appView, selectedEmployee, handleEmployeeChange]);

  // Handle Add New functionality
  const handleAddNew = useCallback(() => {
    // 1. Round up to next 30-minute interval (hel/halv timer)
    const now = (currentView === 'day') ? new Date(currentDate) : new Date();
    const minutes = now.getMinutes();
    const roundedMinutes = Math.ceil(minutes / 30) * 30;
    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0, 0, 0);
    } else {
      now.setMinutes(roundedMinutes, 0, 0);
    }
    const start = new Date(now);
    const end = new Date(start.getTime() + 60 * 60 * 1000); // +1 hour

    const placeholder = createMarkedPlaceholder(start, end);
    setEvents(prev => [
      ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
      placeholder
    ]);
    setSlotInfo({ start, end, tempId: placeholder.logId });
    setModalPosition(getModalPositionStyle(start, currentView));
    setShowModal(true);
  }, [currentView, currentDate, setEvents, setSlotInfo, setModalPosition, setShowModal, getModalPositionStyle, createMarkedPlaceholder]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only trigger if not typing in an input or textarea and modal is not open
      if (
        e.key === 'n' &&
        appView !== 'calendar' &&
        !showModal &&
        document.activeElement.tagName !== 'INPUT' &&
        document.activeElement.tagName !== 'TEXTAREA'
      ) {
        handleAddNew();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal, handleAddNew, appView]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        contextMenuRef.current &&
        !contextMenuRef.current.contains(event.target)
      ) {
        setContextMenu({ visible: false, x: 0, y: 0, event: null });
      }
      
      if (
        slotContextMenuRef.current &&
        !slotContextMenuRef.current.contains(event.target)
      ) {
        setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
      }
    };

    if (contextMenu.visible || slotContextMenu.visible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu.visible, slotContextMenu.visible]);

  const isDraggable = useCallback((event) => !event.isPeopleLeave, []);
  const isResizable = useCallback((event) => !event.isPeopleLeave, []);

  const calendarRef = useRef(null);
  const hasAutoScrolledRef = useRef(false);
  const previousViewRef = useRef(currentView);
  const previousDateRef = useRef(currentDate);
  const previousAuthRef = useRef(authenticated);
  const previousTabRef = useRef({ showProjects, showPeople, showLeave });
  const previousAppViewRef = useRef(appView);

  // Reusable function to scroll calendar to specific time
  const scrollToTime = useCallback((targetTime, smooth = false) => {
    if (!calendarRef.current) return;

    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');
    if (!scrollContainer) return;

    // Extract hour from target time
    const targetHour = typeof targetTime === 'number' ? targetTime : targetTime.getHours();
    const targetMinutes = typeof targetTime === 'number' ? 0 : targetTime.getMinutes();
    
    // Calculate total minutes from start of day
    const totalMinutes = targetHour * 60 + targetMinutes;
    
    // Try to calculate actual pixels per hour from DOM
    const timeSlots = scrollContainer.querySelectorAll('.rbc-time-slot');
    let pixelsPerHour = 40; // fallback
    
    if (timeSlots.length > 0) {
      const slotHeight = timeSlots[0].offsetHeight;
      const slotsPerHour = 2; // 30-minute intervals = 2 slots per hour
      pixelsPerHour = slotHeight * slotsPerHour;
    }
    
    // Calculate scroll position - add some padding above for context
    const paddingHours = 1; // Show 1 hour above for context
    const scrollPosition = Math.max(0, (totalMinutes / 60 - paddingHours) * pixelsPerHour);
    
    console.log('Scrolling to time:', {
      targetTime: typeof targetTime === 'number' ? `${targetTime}:00` : targetTime.toTimeString(),
      targetHour,
      targetMinutes,
      totalMinutes,
      pixelsPerHour,
      scrollPosition,
      smooth
    });
    
    if (smooth) {
      scrollContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    } else {
      scrollContainer.scrollTop = scrollPosition;
    }
  }, []);

  useLayoutEffect(() => {
    if (!calendarRef.current || showModal) return; // <- skip if modal is open

    const scrollContainer = calendarRef.current.querySelector('.rbc-time-content');

    if (scrollContainer) {
      const viewChanged = previousViewRef.current !== currentView;
      const dateChanged = previousDateRef.current !== currentDate;
      const authChanged = previousAuthRef.current !== authenticated;
      const tabChanged = previousTabRef.current.showProjects !== showProjects
        || previousTabRef.current.showPeople !== showPeople
        || previousTabRef.current.showLeave !== showLeave;
      const appViewChanged = previousAppViewRef.current !== appView;

      if (!hasAutoScrolledRef.current || viewChanged || dateChanged || authChanged || tabChanged || appViewChanged) {
        const targetHour = showLeave ? 0 : 8;
        scrollToTime(targetHour, false);
        hasAutoScrolledRef.current = true;
      }

      previousViewRef.current = currentView;
      previousDateRef.current = currentDate;
      previousAuthRef.current = authenticated;
      previousTabRef.current = { showProjects, showPeople, showLeave };
      previousAppViewRef.current = appView;
    }
  }, [currentView, showModal, currentDate, scrollToTime, authenticated, showProjects, showPeople, showLeave, appView]);

  // react-big-calendar only measures gutter width on mount (componentDidMount).
  // Window resize never triggers remeasurement, causing header/column misalignment.
  // Fix: remount the calendar (bump calendarKey) shortly after the user stops resizing.
  useEffect(() => {
    let timeoutId;
    const onResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => setCalendarKey(k => k + 1), 150);
    };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); clearTimeout(timeoutId); };
  }, []);

  useAuthBootstrap({
    authenticated,
    setAuthenticated,
    setPortalName,
    setProjects,
    setEvents,
    showError,
    setError,
    setBillableAutoFill,
    setCurrentView,
    setFeatureFlags,
  });

  // Save user's view preference whenever currentView changes
  useEffect(() => {
    if (authenticated && currentView) {
      API.saveUserViewPreference(currentView).catch(() => {
        // Silently fail - not critical
      });
    }
  }, [currentView, authenticated]);


  const fetchProjects = useCallback(() => {
    API.fetchProjects()
      .then((projectData) => {
        if (projectData.success) {
          setProjects(projectData.data);
        } else {
          showError('Projects Error', 'Failed to fetch projects');
        }
      })
      .catch((err) => {
        showError('Connection Error', err.message || 'Failed to fetch projects. Please check your connection.');
      });
  }, [showError]);

  const handleConvertCRMSync = useCallback(async (eventData) => {
    const crmLogId = eventData.logId; // Keep the 'crm:' or 'projects:crm:' prefix
    
    try {
      // First, create the new Projects timelog
      const createData = await API.createTimelog({
        start: eventData.start,
        end: eventData.end,
        projectId: eventData.projectId,
        timecodeId: eventData.timecodeId,
        notes: eventData.title,
        bill_status: eventData.billingtype,
        date: eventData.start.toISOString().split('T')[0],
      });

      if (createData.success) {
        // ✅ Delete using the FULL logId (with prefix)
        console.log('Converting CRM sync - deleting:', crmLogId);
        await API.deleteTimelog({
          logId: crmLogId, // Pass with full prefix
          projectId: eventData.projectId,
          timecodeId: eventData.timecodeId,
        });

        // Update UI: remove old CRM event and add new Projects event
        setEvents((prev) => {
          const filtered = prev.filter(ev => ev.logId !== eventData.logId);
          const newEvent = {
            title: eventData.title,
            start: eventData.start,
            end: eventData.end,
            projectId: eventData.projectId,
            timecodeId: eventData.timecodeId,
            projectName: eventData.projectName || '',
            timecodeName: eventData.timecodeName || '',
            billingtype: eventData.billingtype || '',
            approval_status: 'Pending',
            logId: `projects:${createData.data.id}`,
          };
          return [...filtered, newEvent];
        });
        
        fetchProjects(); // Refresh projects list
      } else {
        showError('Conversion Failed', 'Failed to convert Outlook sync to project timelog.');
      }
    } catch (error) {
      console.error('Error converting CRM sync:', error);
      showError('Conversion Error', error.message || 'An error occurred while converting the Outlook sync.');
    }
  }, [fetchProjects, showError]);

  const eventPropGetter = useCallback((event) => {
    let backgroundColor = '#a9bec1'; 
    let borderStyle = 'solid';
    let borderColor = 'rgba(65, 60, 65, 0.4)';
    if (event.isPeopleLeave) {
      if (event.leaveStatus === 'Approved') {
        backgroundColor = '#c8e6c9';
        borderColor = '#66bb6a';
      } else if (event.leaveStatus === 'Rejected') {
        backgroundColor = '#ef4444';
        borderColor = '#ef4444';
      } else {
        // Pending
        backgroundColor = '#cfd8dc';
        borderColor = '#78909c';
      }
    }
    else if (event.isHR) {
      backgroundColor = '#fce4ec';
      borderColor = '#f48fb1';
    }
    else if (event.isPeople) {
      backgroundColor = '#f7d6e0';
      borderColor = '#e48bb7';
    }
    else if (event.timecodeName === '(SYNC) Outlook Sync' || String(event.id || event.logId || '').startsWith('crm:') || String(event.id || event.logId || '').startsWith('projects:crm:')) {
      backgroundColor = '#d0e1f9';
    } else if (event.approval_status === 'Approved') {
      backgroundColor = event.billingtype === 'Billable' ? '#e8eddf' : '#e0dabb';
    } else if (event.approval_status === 'Rejected' || event.approval_status === 'Changed') {
      backgroundColor = '#767279';
    }

    if (event.approval_status === 'Marked') {
      backgroundColor = 'rgba(204, 183, 120, 0.15)';
      borderStyle = 'dashed';
      borderColor = '#ccb778';
    } else if (editingEvent?.logId === event.logId) {
      backgroundColor = '#fffbe6';
      borderStyle = 'solid';
      borderColor = '#ccb778';
    }

    return {
      style: {
        backgroundColor,
        color: event.approval_status === 'Rejected' ? 'white' : 'black',
        borderRadius: '4px',
        border: `1px ${borderStyle} ${borderColor}`,
        padding: '2px 4px',
        boxShadow: event.approval_status === 'Marked'
          ? '0 0 0 2px #ccb77833'
          : '0 0 10px 0 rgba(0, 0, 0, 0.2)',
        opacity: event.approval_status === 'Marked' ? 0.95 : 1,
      },
    };
  }, [editingEvent]);

  const {
    totalsCurrentView,
    totalsCurrentViewPeople,
    totalsYTD,
    totalsYTDPeople,
    leaveDaysYTD,
    totalsCurrentWeek,
    totalsCurrentWeekPeople,
    leaveDaysCurrentWeek,
    getDailyTotals,
    getCustomPeriodTotals,
    getLeaveCustomPeriod,
    getYearOverYearComparison,
  } = useCalendarMetrics({ events, currentDate, currentView, enablePeople: featureFlags.enablePeople });

  const handleCustomDateRangeApply = useCallback((range) => {
    setIsLoadingCustomRange(true);
    setCustomDateRange({ ...range, context: customRangeContext }); // NEW

    try {
      if (range.compareWithPreviousYear) {
        const comparisonData = getYearOverYearComparison(range.start, range.end, customRangeContext); // NEW
        setCustomRangeData({
          ...comparisonData,
          range: { ...range, context: customRangeContext }, // NEW
          label: range.label || 'Custom Range',
        });
      } else {
        const currentPeriod = getCustomPeriodTotals(range.start, range.end, customRangeContext); // NEW
        setCustomRangeData({
          current: currentPeriod,
          range: { ...range, context: customRangeContext }, // NEW
          label: range.label || 'Custom Range',
          hasData: currentPeriod.total > 0,
        });
      }
    } catch (error) {
      console.error('Error calculating custom range data:', error);
      showError('Calculation Error', error.message || 'An error occurred while calculating the selected period');
    } finally {
      setIsLoadingCustomRange(false);
    }
  }, [customRangeContext, showError, getCustomPeriodTotals, getYearOverYearComparison]);

  const handleEventDrop = useCallback(({ event, start, end, allDay }) => {
    setIsDragging(false);
    setModalPosition(getModalPositionStyle(start, currentView));
    setOriginalEventBeforeEdit(prev => prev ?? event);
    
    // Check if the new duration would exceed 23 hours
    const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
    if (durationHours > 23) {
      showError('Duration Limit', 'Events cannot exceed 23 hours.');
      return;
    }
    
    // If it's a marked (temporary) event, remove it and create a new marked event at the new spot
    const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
      if (isMarked) {
        setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
        const tempId = `temp-${Date.now()}`;
        const placeholder = {
          title: '',
          start,
          end,
          allDay,
          logId: tempId,
          approval_status: 'Marked',
          projectId: '',
          timecodeId: '',
          billingtype: '',
          projectName: '',
          timecodeName: '',
        };
        setEvents(prev => [...prev, placeholder]);
        setSlotInfo({ start, end, tempId });
        setEditingEvent(null);
        setShowModal(true);
        setOriginalEventBeforeEdit(null);
        return;
      }
      if (event.approval_status !== 'Marked') {
        setEditingEvent(event);
        // Remove marked events if selected is not marked
        setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
      }
      // Normal event: open modal for editing
      const updated = { ...event, start, end, allDay, approval_status: 'Changed' };
      setEditingEvent(updated);
      setShowModal(true);
      setEvents((prev) =>
        prev.map((ev) => (ev === event ? updated : ev))
      );
    }, [currentView]);

  const handleEventResize = useCallback(({ event, start, end }) => {
    setIsDragging(false);
    setOriginalEventBeforeEdit(prev => prev ?? event);
    
    // Check if the new duration would exceed 23 hours
    const durationHours = (new Date(end) - new Date(start)) / (1000 * 60 * 60);
    if (durationHours > 23) {
      showError('Duration Limit', 'Events cannot exceed 23 hours.');
      return;
    }
    
    // If it's a marked (temporary) event, remove it and create a new marked event at the new spot
    const isMarked = event.approval_status === 'Marked' || (event.logId && event.logId.startsWith('temp-'));
    if (isMarked) {
      setEvents(prev => prev.filter(ev => ev.logId !== event.logId));
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start,
        end,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        timecodeId: '',
        billingtype: '',
        projectName: '',
        timecodeName: '',
      };
      setEvents(prev => [...prev, placeholder]);
      setSlotInfo({ start, end, tempId });
      setEditingEvent(null);
      setShowModal(true);
      setOriginalEventBeforeEdit(null);
      return;
    }

    if (event.approval_status !== 'Marked') {
      setEditingEvent(event);
      // Remove marked events if selected is not marked
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    // Normal event: open modal for editing
    const updated = { ...event, start, end, approval_status: 'Changed' };
    //setOriginalEventBeforeEdit(event);
    setEditingEvent(updated);
    setShowModal(true);
    setEvents((prev) =>
      prev.map((ev) => (ev === event ? updated : ev))
    );
  }, []);

  const refreshProjectsOnly = useCallback(async () => {
    const empZuid = selectedEmployee?.Zuid || null;
    try {
      const projectsData = await API.refreshTimelogs(empZuid);
      const allLogs = projectsData.data?.flatMap(group => group.timelogs || []) || [];
      const projectEvents = transformTimelogsToEvents({ timelogs: { date: allLogs } });
      setEvents(prev => [
        ...prev.filter(ev => ev.isPeople || ev.isPeopleLeave || ev.isHR),
        ...projectEvents,
      ]);
    } catch {
      // silently ignore
    }
  }, [selectedEmployee]);

  const handleSaveEvent = useCallback((newEvent) => {
    API.createTimelog({
      start: newEvent.start,
      end: newEvent.end,
      projectId: newEvent.projectId,
      timecodeId: newEvent.timecodeId,
      notes: newEvent.title,
      bill_status: newEvent.billingtype,
      date: newEvent.start.toISOString().split('T')[0],
      taskId: newEvent.taskId || null,
      markTaskDone: newEvent.markTaskDone || false,
    })
      .then((data) => {
        if (data.success) {
          const newEventWithId = {
            title: newEvent.title,
            start: newEvent.start,
            end: newEvent.end,
            projectId: newEvent.projectId,
            timecodeId: newEvent.timecodeId,
            projectName: newEvent.projectName || '',
            timecodeName: newEvent.timecodeName || '',
            billingtype: newEvent.billingtype || '',
            approval_status: 'Pending',
            logId: `projects:${data.data.id}`,
            accountId: newEvent.accountId || '',
            accountName: newEvent.accountName || '',
            taskId: newEvent.taskId || null,
            taskName: newEvent.taskName || '',
          };
          setEvents((prev) => [...prev, newEventWithId]);
          fetchProjects();
          showSuccess('Tidsregistrering indsendt', '');
          if (featureFlags.enableRolle && newEvent.taskId && newEvent.projectId) {
            API.assignRolle(newEvent.projectId, newEvent.taskId).catch(() => {});
          }
          setTimeout(() => refreshProjectsOnly(), 5000);
        } else {
          showError('Gem fejlede', data.message || 'Kunne ikke gemme registrering. Prøv igen.');
        }
      })
      .catch((err) => {
        showError('Gem fejlede', err.message || 'Serverfejl under gem. Tjek din forbindelse og prøv igen.');
      });
  }, [fetchProjects, showError, refreshProjectsOnly]);

  const handleSaveLeave = useCallback(async (leaveData) => {
    const data = await API.createLeave({
      leaveTypeId: leaveData.leaveType,
      leaveStartDate: leaveData.leaveStartDate,
      leaveEndDate: leaveData.leaveEndDate,
      leaveNotes: leaveData.leaveNotes || '',
    });
    if (!data.success) {
      throw new Error(data.message || 'Kunne ikke sende fraværsanmodning. Prøv igen.');
    }
    showSuccess('Fravær afsendt', 'Din fraværsanmodning er sendt til godkendelse.');
    const empZuid = selectedEmployee?.Zuid || null;
    API.fetchLeaveRequests(empZuid).then(leaveResp => {
      const leaveItems = Array.isArray(leaveResp.data) ? leaveResp.data : [];
      const freshLeaveEvents = leaveItems.flatMap(transformLeaveToEvent);
      setEvents(prev => [...prev.filter(ev => !ev.isPeopleLeave), ...freshLeaveEvents]);
    }).catch(() => {});
  }, [showSuccess, selectedEmployee]);

  const handleUpdateEvent = useCallback((updatedEvent) => {
    API.updateTimelog({
      start: updatedEvent.start,
      end: updatedEvent.end,
      projectId: updatedEvent.projectId,
      timecodeId: updatedEvent.timecodeId,
      notes: updatedEvent.title,
      bill_status: updatedEvent.billingtype,
      date: updatedEvent.start.toISOString().split('T')[0],
      logId: stripPrefix(updatedEvent.logId),
      taskId: updatedEvent.taskId || null,
      markTaskDone: updatedEvent.markTaskDone || false,
    })
      .then((data) => {
        if (data.success) {
          setEvents((prev) =>
            prev.map((ev) =>
              ev.logId === updatedEvent.logId ? { ...ev, ...updatedEvent } : ev
            )
          );
          setOriginalEventBeforeEdit(null);
          showSuccess('Tidsregistrering opdateret', '');
        } else {
          showError('Opdatering fejlede', data.message || 'Kunne ikke opdatere registrering. Prøv igen.');
        }
      })
      .catch((err) => {
        showError('Opdatering fejlede', err.message || 'Serverfejl under opdatering. Tjek din forbindelse og prøv igen.');
      });
  }, [showError, showSuccess]);

  const handleDeleteEvent = useCallback((logId, projectId, timecodeId, isPeople, taskId) => {
    if (String(logId).startsWith('hr:')) {
      setEvents((prev) => prev.filter((ev) => ev.logId !== logId));
      return;
    }
    if (isPeople) {
      const ev = events.find(e => e.logId === logId);
      API.deletePeopleTimelog({ timeLogId: stripPrefix(logId), employeeEmail: ev?.employeeEmail || undefined })
        .then((data) => {
          if (data.success) {
            setEvents((prev) => prev.filter((ev) => ev.logId !== logId));
          } else {
            showError('Sletning fejlede', data.message || 'Kunne ikke slette Zoho People registrering. Prøv igen.');
          }
        })
        .catch((err) => {
          showError('Forbindelsesfejl', err?.message || 'Serverfejl under sletning af Zoho People registrering.');
        });
      return;
    }

    const isCRMLog = logId.toString().includes('crm:');
    const cleanLogId = isCRMLog ? logId : stripPrefix(logId);

    API.deleteTimelog({ logId: cleanLogId, projectId, taskId: taskId || null })
      .then((data) => {
        if (data.success) {
          setEvents((prev) => prev.filter((ev) => ev.logId !== logId));
        } else {
          showError('Sletning fejlede', 'Kunne ikke slette registrering. Prøv igen.');
        }
      })
      .catch((err) => {
        showError('Forbindelsesfejl', err?.message || 'Serverfejl under sletning.');
      });
  }, [showError, stripPrefix]);

  const handleRemoveOutlookSyncs = useCallback(() => {
    // Helper function to identify Outlook sync events
    const isOutlookSyncEvent = (event) => {
      if (event.timecodeName === '(SYNC) Outlook Sync') return true;
      const idString = String(event.id || event.logId || '');
      if (idString.startsWith('crm:') || idString.startsWith('projects:crm:')) return true;
      return false;
    };

    // Helper function to check if event is in current view
    const isInCurrentView = (event) => {
      if (currentView === 'work_week') {
        const weekStart = moment(currentDate).startOf('week').add(0, 'days'); // Monday
        const weekEnd = moment(currentDate).startOf('week').add(4, 'days').endOf('day'); // Friday
        return moment(event.start).isBetween(weekStart, weekEnd, null, '[]');
      }

      const viewStart = moment(currentDate).startOf(
        currentView === 'month'
          ? 'month'
          : currentView === 'week'
          ? 'week'
          : 'day'
      );
      const viewEnd = moment(currentDate).endOf(
        currentView === 'month'
          ? 'month'
          : currentView === 'week'
          ? 'week'
          : 'day'
      );
      return moment(event.start).isBetween(viewStart, viewEnd, null, '[]');
    };

    // Find all Outlook sync events in the current view
    const outlookSyncsInView = events.filter(event => 
      isOutlookSyncEvent(event) && isInCurrentView(event)
    );

    if (outlookSyncsInView.length === 0) {
      showError('Ingen synkroniseringer', 'Der er ingen Outlook-synkroniseringer i den aktuelle visning.');
      return;
    }

    // Show confirmation modal
    setOutlookSyncsToRemove(outlookSyncsInView);
    setShowRemoveSyncsConfirm(true);
  }, [events, currentDate, currentView, showError]);

  const handleConfirmRemoveSyncs = useCallback(async () => {
    setShowRemoveSyncsConfirm(false);

    // Optimistically remove all events from UI immediately
    const eventsToRemove = outlookSyncsToRemove;
    const logIdsToRemove = new Set(eventsToRemove.map(e => e.logId));
    
    setEvents(prev => prev.filter(ev => !logIdsToRemove.has(ev.logId)));
    setOutlookSyncsToRemove([]);

    // Delete all Outlook sync events in the background
    let successCount = 0;
    let errorCount = 0;
    const failedEvents = [];

    // Process all deletions in parallel
    const deletionPromises = eventsToRemove.map(async (event) => {
      try {
        const isCRMLog = event.logId.toString().includes('crm:');
        const cleanLogId = isCRMLog ? event.logId : stripPrefix(event.logId);

        const response = await API.deleteTimelog({ 
          logId: cleanLogId, 
          projectId: event.projectId, 
          timecodeId: event.timecodeId 
        });

        if (response.success) {
          successCount++;
          return { success: true, event };
        } else {
          errorCount++;
          failedEvents.push(event);
          return { success: false, event };
        }
      } catch (error) {
        console.error('Error deleting Outlook sync event:', error);
        errorCount++;
        failedEvents.push(event);
        return { success: false, event };
      }
    });

    await Promise.all(deletionPromises);

    // Restore failed deletions back to the UI
    if (failedEvents.length > 0) {
      setEvents(prev => [...prev, ...failedEvents].sort((a, b) => 
        new Date(a.start) - new Date(b.start)
      ));
    }

  }, [outlookSyncsToRemove, stripPrefix]);

  const handleCancelRemoveSyncs = useCallback(() => {
    setShowRemoveSyncsConfirm(false);
    setOutlookSyncsToRemove([]);
  }, []);

  const handleNavigate = useCallback((newDate) => {
    setCurrentDate(newDate);
  }, []);

  const handleUpdatePeopleEvent = useCallback((updatedTimelogDetails) => {
    console.log('Zoho People updated event:', updatedTimelogDetails);
    const updatedEvent = handlePeopleTimelogToEvent(updatedTimelogDetails);
    if (!updatedEvent) return;

    console.log('Updated event:', updatedEvent);

    setEvents(prev =>
      prev.map(ev => (ev.logId === updatedEvent.logId ? { ...ev, ...updatedEvent } : ev))
    );

    suppressRevertRef.current = true; // <-- prevent revert on modal close
    setOriginalEventBeforeEdit(null);
  }, []);

  const max = useMemo(() => new Date(2025, 4, 10, 23, 59), []);
  const timeslots = 2;
  const views = {
    month: true,
    week: true,
    work_week: true,
    day: true,
    list: true
  };

  // Handler for day click in month view - navigates to day view
  const handleDayClick = useCallback((date) => {
    setCurrentDate(date);
    setCurrentView('day');
  }, []);

  const handleSelectSlot = useCallback((slotInfo, e) => {
    // In month view, clicking on a date should navigate to day view
    if (currentView === 'month') {
      handleDayClick(slotInfo.start);
      return;
    }

    // Check if this is a right-click
    if (e && e.type === 'contextmenu') {
      handleRightClickSlot(slotInfo, e);
      return;
    }

    // If modal is already open, don't create a new event
    if (showModal) {
      return;
    }

    // Restore previous event if editing and changes were not saved
    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setEditingEvent(null);
    setIsDragging(false);
    setSelectedSlotDate(slotInfo.start);
    setModalPosition(getModalPositionStyle(slotInfo.start, currentView));

    if (eventToCopy) {
      const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
      const newEnd = moment(slotInfo.start).add(duration, 'ms').toDate();
      const copiedEvent = {
        ...eventToCopy,
        start: slotInfo.start,
        end: newEnd,
      };
      handleSaveEvent(copiedEvent);
      setEventToCopy(null);
    } else {
      const tempId = `temp-${Date.now()}`;
      const placeholder = {
        title: '',
        start: slotInfo.start,
        end: slotInfo.end,
        logId: tempId,
        approval_status: 'Marked',
        projectId: '',
        timecodeId: '',
        billingtype: '',
        projectName: '',
        timecodeName: '',
      };
      setEvents(prev => [
        ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
        placeholder
      ]);
      setSlotInfo({ start: slotInfo.start, end: slotInfo.end, tempId });
      setShowModal(true);
    }
  }, [eventToCopy, handleSaveEvent, currentView, originalEventBeforeEdit, handleDayClick, handleRightClickSlot, showModal]);

  // Pre-resolve accountId/accountName from preloaded assignedProjects so the modal
  // opens with complete data and doesn't have to derive it (eliminates race conditions
  // with the form's accountId useEffect).
  const enrichEventWithAccount = useCallback((event) => {
    if (!event) return event;
    if (event.accountId) return event;
    if (!featureFlags.filterProjectsByAssignment) return event;
    if (!event.projectId || !preloadedAssignedProjects) return event;
    const found = preloadedAssignedProjects.find(p => String(p.id) === String(event.projectId));
    if (!found) return event;
    return {
      ...event,
      accountId: found.accountId || found.fallbackAccountId || '',
      accountName: found.accountName || found.fallbackAccountName || '',
    };
  }, [featureFlags.filterProjectsByAssignment, preloadedAssignedProjects]);

  const handleSelectEvent = useCallback((event) => {
    // Restore previous event if editing and changes were not saved
    if (originalEventBeforeEdit) {
      setEvents(prev =>
        prev.map(ev =>
          ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
        )
      );
      setOriginalEventBeforeEdit(null);
    }

    setIsDragging(false);
    setShowDeleteConfirm(false); 
    setEventPendingDelete(null); 
    setOriginalEventBeforeEdit(event);

    // Only set editing event if it's not a marked event
    if (event.approval_status !== 'Marked') {
      setEditingEvent(enrichEventWithAccount(event));
      // Remove marked events if selected is not marked
      setEvents((prev) => prev.filter(ev => ev.approval_status !== 'Marked'));
    }
    setShowModal(true);
  }, [originalEventBeforeEdit, enrichEventWithAccount]);

  const handleModalTimeChange = useCallback((updatedEvent) => {
    setEvents((prevEvents) =>
      prevEvents.map(ev =>
        // For editing: match by logId, for marked: match by tempId or start/end
        (updatedEvent.logId && ev.logId === updatedEvent.logId) ||
        (!updatedEvent.logId && slotInfo?.tempId && ev.logId === slotInfo.tempId)
          ? { ...ev, start: updatedEvent.start, end: updatedEvent.end }
          : ev
      )
    );
    // Also update editingEvent if present
    setEditingEvent((prev) =>
      prev && prev.logId === updatedEvent.logId
        ? { ...prev, start: updatedEvent.start, end: updatedEvent.end }
        : prev
    );
    // Also update slotInfo for marked events
    if (!updatedEvent.logId && slotInfo) {
      setSlotInfo((prev) => prev ? { ...prev, start: updatedEvent.start, end: updatedEvent.end } : prev);
    }
    
    // 🚀 Auto-scroll calendar to follow the event when moved with arrow keys
    if (updatedEvent.start && (currentView === 'day' || currentView === 'week' || currentView === 'work_week')) {
      // Use smooth scrolling for better UX
      scrollToTime(updatedEvent.start, true);
    }
  }, [slotInfo, currentView, scrollToTime]);

  // Memoize calendar sub-components (headers, event renderers, month cell)
  const calendarComponents = useMemo(() => createCalendarComponents({
    currentView,
    formatHours,
    getDailyTotals,
    handleDayClick,
    handleRightClickEvent,
    setupLongPress,
    showPeople,
    showLeave,
  }), [currentView, getDailyTotals, handleDayClick, handleRightClickEvent, setupLongPress, showPeople, showLeave]);
  
  if (error && (error.includes('Error 1') || error.includes('Error 2') || error.includes('Error 3'))) {
    let title, message, details;
    
    if (error.includes('Error 1')) {
      title = 'Godkendelse påkrævet';
      message = 'Kunne ikke bekræfte dine adgangsoplysninger.';
      details = 'Godkendelsesparameteret mangler i din URL.';
    } else if (error.includes('Error 2')) {
      title = 'Login fejlede';
      message = 'Godkendelse kunne ikke gennemføres.';
      details = 'Dine loginoplysninger kunne ikke bekræftes af serveren.';
    } else if (error.includes('Error 3')) {
      title = 'Forbindelsesfejl';
      message = 'Kunne ikke oprette forbindelse til godkendelsesserveren.';
      details = 'Tjek din internetforbindelse og prøv igen.';
    }

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <div className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.924-.833-2.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
            <p className="text-gray-600 mb-4">{message}</p>
            <p className="text-sm text-gray-500 mb-6">{details}</p>
            <div className="bg-[#d6c08b] rounded-lg p-4">
              <p className="text-sm text-white">
                If the issue persists, please contact the IT department.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <AuthenticatingModal isOpen={true} />;
  }

  


  return (
    <div ref={calendarRef} className="h-screen flex flex-col bg-white app-container">
      {/* Responsive Navigation */}
      <MobileNavigation
        currentView={currentView}
        setCurrentView={setCurrentView}
        currentDate={currentDate}
        setCurrentDate={setCurrentDate}
        handleNavigate={handleNavigate}
        onAddNew={handleAddNew}
        onCreateSeries={handleCreateSeriesFromHeader}
        enableSeries={featureFlags.enableSeries}
        enablePeople={featureFlags.enablePeople}
        enableLeave={featureFlags.enableLeave}
        enableOutlookSync={featureFlags.enableOutlookSync}
        showPeople={showPeople}
        setShowPeople={setShowPeople}
        showProjects={showProjects}
        setShowProjects={setShowProjects}
        showLeave={showLeave}
        setShowLeave={setShowLeave}
        showOutlookSync={showOutlookSync}
        setShowOutlookSync={setShowOutlookSync}
        onRemoveOutlookSyncs={handleRemoveOutlookSyncs}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
        demoMode={featureFlags.demoMode}
        appView={appView}
        setAppView={(view) => {
          if (appView === 'form' && standaloneHasChanges) {
            setPendingAppView(view);
            setShowStandaloneUnsavedModal(true);
          } else {
            setAppView(view);
          }
        }}
        isAdmin={featureFlags.isAdmin}
        employees={featureFlags.employees}
        selectedEmployee={selectedEmployee}
        onEmployeeChange={handleEmployeeChange}
        currentUserName={featureFlags.currentUserName}
      />

      {/* Standalone Form View */}
      {appView === 'form' && (
        <div className="flex-1 overflow-y-auto flex justify-center pt-20 md:pt-10 pb-6 px-4 bg-gray-50">
          <EventFormModal
            key={`standalone-${standaloneKey}`}
            standalone
            isOpen={true}
            onClose={() => {}}
            onSave={async (eventData) => {
              if (eventData.sendToHR) {
                setEvents(prev => [...prev, {
                  title: eventData.title || 'Arbejdstid til HR',
                  start: eventData.start,
                  end: eventData.end,
                  logId: `hr:${Date.now()}`,
                  isHR: true,
                }]);
                setStandaloneKey(k => k + 1);
              } else if (eventData.isWorkdaySaved) {
                showSuccess('Arbejdstid gemt', 'Din arbejdstid er registreret.');
                const empEmail = selectedEmployee?.email || null;
                API.refreshPeopleTimelogs(empEmail).then(peopleResp => {
                  const peopleLogs = peopleResp.data?.flatMap(g => g.timelogs || []) || [];
                  const freshPeopleEvents = peopleLogs.map(log => handlePeopleTimelogToEvent(log)).filter(Boolean);
                  setEvents(prev => [...prev.filter(ev => !ev.isPeople), ...freshPeopleEvents]);
                }).catch(() => {});
              } else if (eventData.isPeopleLeave) {
                await handleSaveLeave(eventData);
              } else {
                handleSaveEvent(eventData);
                setStandaloneKey(k => k + 1);
              }
            }}
            onDelete={handleDeleteEvent}
            start={standaloneStart}
            end={standaloneEnd}
            projects={projects}
            currentView={currentView}
            billableAutoFill={billableAutoFill}
            enableLeave={featureFlags.enableLeave}
            enablePeople={featureFlags.enablePeople}
            directProjectMode={featureFlags.directProjectMode}
            filterProjectsByAssignment={featureFlags.filterProjectsByAssignment}
            preloadedAssignedProjects={preloadedAssignedProjects}
            isAdmin={featureFlags.isAdmin}
            onRefreshProjects={(data) => setProjects(data)}
            modalPosition={{}}
            setModalPosition={() => {}}
            getModalPositionStyle={() => ({})}
            onHasChangesChange={(hasChanges, timerTab) => {
              setStandaloneHasChanges(hasChanges);
              setStandaloneTimerTab(timerTab);
            }}
          />
        </div>
      )}

      <ConfirmModal
        isOpen={showStandaloneUnsavedModal}
        onConfirm={() => {
          setShowStandaloneUnsavedModal(false);
          setStandaloneHasChanges(false);
          setStandaloneTimerTab(null);
          setStandaloneKey(k => k + 1);
          setAppView(pendingAppView);
        }}
        onCancel={() => setShowStandaloneUnsavedModal(false)}
        title="Ugemte ændringer"
        message={
          standaloneTimerTab === 'projects' ? 'Du har ugemte ændringer i din projekt tidslog. Vil du forkaste dem?' :
          standaloneTimerTab === 'workday' ? 'Du har ugemte ændringer i din arbejdstid tidslog. Vil du forkaste dem?' :
          'Er du sikker på at du vil forkaste dine ændringer?'
        }
        confirmText="Forkast"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Presence View */}
      {appView === 'presence' && (
        <PresenceView currentUserName={featureFlags.currentUserName} />
      )}

      {/* Calendar Container */}
      {appView === 'calendar' && <div
        className="flex-1 calendar-container-mobile md:calendar-container-tablet lg:calendar-container-desktop"
        onContextMenu={(e) => {
          // Check if the right-click was on a calendar slot (not on an event)
          const target = e.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');
          
          if (!isEvent && isSlot && currentView !== 'month') {
            // Calculate the actual slot time from mouse position
            // Use a much simpler approach - just calculate from pixel position
            const calendarRect = e.currentTarget.getBoundingClientRect();
            const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
            
            if (timeColumn) {
              const columnRect = timeColumn.getBoundingClientRect();
              const mouseY = e.clientY - columnRect.top;
              
              // Get the time gutter to understand the time scale
              const timeView = e.currentTarget.querySelector('.rbc-time-view');
              const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;
              
              let startHour = 0; // Default start hour (midnight)
              let endHour = 24; // Default end hour  
              let totalHours = 24; // Default hours shown (full day)
              
              if (timeGutter) {
                // Try to get actual start/end from time labels
                const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                if (timeLabels.length > 0) {
                  // Get first time
                  const firstTimeText = timeLabels[0].textContent?.trim();
                  if (firstTimeText && firstTimeText.includes(':')) {
                    const match = firstTimeText.match(/(\d+):/);
                    if (match) {
                      startHour = parseInt(match[1]);
                    }
                  }
                  
                  // Get last time  
                  const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                  if (lastTimeText && lastTimeText.includes(':')) {
                    const match = lastTimeText.match(/(\d+):/);
                    if (match) {
                      endHour = parseInt(match[1]) + 1; // Add 1 to get end hour
                    }
                  }
                  
                  totalHours = endHour - startHour;
                  console.log(`Calendar time range: ${startHour}:00 - ${endHour}:00 (${totalHours} hours)`);
                }
              }
              
              // Simple pixel-based calculation
              const percentDown = mouseY / columnRect.height;
              const hoursFromStart = percentDown * totalHours;
              const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
              const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30;
              const finalHour = startHour + Math.floor(roundedMinutes / 60);
              const finalMinute = roundedMinutes % 60;
              
              console.log(`Simple calculation: mouseY=${mouseY}, height=${columnRect.height}, percent=${percentDown.toFixed(3)}, hours=${hoursFromStart.toFixed(2)} → ${finalHour}:${finalMinute.toString().padStart(2, '0')}`);
            
              // Create the slot start time for today (or current date in view)
              const slotStart = new Date(currentDate);
              slotStart.setHours(finalHour, finalMinute, 0, 0);
             
              // If we're in day view and it's a different day, use that day
              if (currentView === 'day') {
                // Use the date from the day view
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              } else if (currentView === 'week' || currentView === 'work_week') {
                // For week views, we need to determine which day column was clicked
                // Find the correct day column selector - react-big-calendar uses different classes
                const timeView = e.currentTarget.querySelector('.rbc-time-view');
                const dayColumns = timeView ? timeView.querySelectorAll('.rbc-time-column') : [];
                let dayIndex = 0;
                
                // Alternative approach: find all day header elements to map positions
                const headers = e.currentTarget.querySelectorAll('.rbc-header');
                
                if (headers.length > 0) {
                  // Use headers to determine day positions
                  for (let i = 0; i < headers.length; i++) {
                    const headerRect = headers[i].getBoundingClientRect();
                    if (e.clientX >= headerRect.left && e.clientX <= headerRect.right) {
                      dayIndex = i;
                      console.log(`Slot clicked: Header ${i} (${headers[i].textContent?.trim()})`);
                      break;
                    }
                  }
                } else if (dayColumns.length > 0) {
                  // Fallback to time columns
                  for (let i = 0; i < dayColumns.length; i++) {
                    const dayRect = dayColumns[i].getBoundingClientRect();
                    if (e.clientX >= dayRect.left && e.clientX <= dayRect.right) {
                      dayIndex = i;
                      console.log(`Slot clicked: Column ${i}`);
                      break;
                    }
                  }
                }
                
                // Calculate the date for this day column
                // Always use isoWeek (Monday start) to match calendar display
                let startOfWeek = moment(currentDate).startOf('isoWeek');
                
                const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
                
                console.log(`Slot calculated: ${moment(slotStart).format('dddd YYYY-MM-DD HH:mm')}`);
              }
             
              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000); // +1 hour
              
              const slotInfo = {
                start: slotStart,
                end: slotEnd
              };
              
              handleRightClickSlot(slotInfo, e);
            }
          }
        }}
        {...setupLongPress((e, syntheticEvent) => {
          // Reuse the same logic as onContextMenu for slots
          const target = syntheticEvent.target;
          const isEvent = target.closest('.rbc-event');
          const isSlot = target.closest('.rbc-time-slot') || target.closest('.rbc-day-slot');
          
          if (!isEvent && isSlot && currentView !== 'month') {
            // Calculate slot time and handle long press like right-click
            const calendarRect = syntheticEvent.currentTarget.getBoundingClientRect();
            const timeColumn = target.closest('.rbc-time-column') || target.closest('.rbc-day-slot');
            
            if (timeColumn) {
              const columnRect = timeColumn.getBoundingClientRect();
              const mouseY = syntheticEvent.clientY - columnRect.top;
              
              const timeView = syntheticEvent.currentTarget.querySelector('.rbc-time-view');
              const timeGutter = timeView ? timeView.querySelector('.rbc-time-gutter') : null;
              
              let startHour = 0;
              let endHour = 24;
              let totalHours = 24;
              
              if (timeGutter) {
                const timeLabels = timeGutter.querySelectorAll('.rbc-time-slot');
                if (timeLabels.length > 0) {
                  const firstTimeText = timeLabels[0].textContent?.trim();
                  if (firstTimeText && firstTimeText.includes(':')) {
                    const match = firstTimeText.match(/(\d+):/);
                    if (match) {
                      startHour = parseInt(match[1]);
                    }
                  }
                  
                  const lastTimeText = timeLabels[timeLabels.length - 1].textContent?.trim();
                  if (lastTimeText && lastTimeText.includes(':')) {
                    const match = lastTimeText.match(/(\d+):/);
                    if (match) {
                      endHour = parseInt(match[1]) + 1;
                    }
                  }
                  
                  totalHours = endHour - startHour;
                }
              }
              
              const percentDown = mouseY / columnRect.height;
              const hoursFromStart = percentDown * totalHours;
              const totalMinutesFromStart = Math.floor(hoursFromStart * 60);
              const roundedMinutes = Math.round(totalMinutesFromStart / 30) * 30;
              const finalHour = startHour + Math.floor(roundedMinutes / 60);
              const finalMinute = roundedMinutes % 60;
              
              const slotStart = new Date(currentDate);
              slotStart.setHours(finalHour, finalMinute, 0, 0);
              
              if (currentView === 'day') {
                const dayDate = new Date(currentDate);
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              } else if (currentView === 'week' || currentView === 'work_week') {
                const timeView = syntheticEvent.currentTarget.querySelector('.rbc-time-view');
                const headers = syntheticEvent.currentTarget.querySelectorAll('.rbc-header');
                let dayIndex = 0;
                
                if (headers.length > 0) {
                  for (let i = 0; i < headers.length; i++) {
                    const headerRect = headers[i].getBoundingClientRect();
                    if (syntheticEvent.clientX >= headerRect.left && syntheticEvent.clientX <= headerRect.right) {
                      dayIndex = i;
                      break;
                    }
                  }
                }
                
                let startOfWeek = moment(currentDate).startOf('isoWeek');
                const dayDate = startOfWeek.clone().add(dayIndex, 'days').toDate();
                slotStart.setFullYear(dayDate.getFullYear(), dayDate.getMonth(), dayDate.getDate());
              }
              
              const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);
              const slotInfo = {
                start: slotStart,
                end: slotEnd
              };
              
              handleRightClickSlot(slotInfo, syntheticEvent);
            }
          }
        })}
      >
{currentView === 'list' ? (
          <ListView
            events={filteredEvents}
            currentDate={currentDate}
            onSelectEvent={handleSelectEvent}
            onRightClickEvent={handleRightClickEvent}
            setupLongPress={setupLongPress}
          />
        ) : (
          <DnDCalendar
            key={calendarKey}
            events={filteredEvents}
            onDragStart={() => setIsDragging(true)}
            localizer={localizer}
            date={currentDate}
            scrollToTime={new Date(0, 0, 0, 8, 0, 0)}
            onNavigate={handleNavigate}
            startAccessor="start"
            endAccessor="end"
            view={currentView}
            onView={setCurrentView}
            views={views}
            max={max}
            step={30}
            snapToSlot={false}
            timeslots={timeslots}
            showMultiDayTimes
            selectable={currentView === 'month'}
            popup
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            onEventDrop={handleEventDrop}
            onEventResize={handleEventResize}
            resizable
            formats={FORMATS}
            eventPropGetter={eventPropGetter}
            draggableAccessor={isDraggable}
            resizableAccessor={isResizable}
            components={calendarComponents}
          />
        )}
      </div>}

      {/* Responsive Stats Footer */}
      {featureFlags.enableStatsBar && appView === 'calendar' && <MobileFooter
        totalsCurrentWeek={totalsCurrentWeek}
        totalsCurrentWeekPeople={totalsCurrentWeekPeople}
        leaveDaysCurrentWeek={leaveDaysCurrentWeek}
        totalsYTD={totalsYTD}
        totalsYTDPeople={totalsYTDPeople}
        leaveDaysYTD={leaveDaysYTD}
        getCustomPeriodTotals={getCustomPeriodTotals}
        getLeaveCustomPeriod={getLeaveCustomPeriod}
        showProjects={showProjects}
        showPeople={showPeople}
        showLeave={showLeave}
      />}

      {/* Context Menu for Events */}
      {contextMenu.visible && (
        <ul
          ref={contextMenuRef}
          className="absolute bg-white border border-gray-200 shadow-lg rounded-md p-2 z-50"
          style={{
            top: contextMenu.y,
            left: contextMenu.x,
          }}
        >
          {/* Only show Copy if not Outlook synced event */}
          {!(contextMenu.event?.timecodeName === '(SYNC) Outlook Sync' || String(contextMenu.event?.id || contextMenu.event?.logId || '').startsWith('crm:') || String(contextMenu.event?.id || contextMenu.event?.logId || '').startsWith('projects:crm:')) && (
            <li
              className="px-2 py-1 cursor-pointer hover:bg-gray-100"
              onClick={() => {
                setEventToCopy(contextMenu.event);
                setContextMenu({ visible: false, x: 0, y: 0, event: null });
              }}
            >
              Copy
            </li>
          )}
          <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100"
            onClick={() => {
              handleCreateSeries(contextMenu.event);
            }}
          >
            Create Series
          </li>
          {/* Show Delete Entire Series if this event is part of a series */}
          {contextMenu.event?.seriesId && (
            <li
              className="px-2 py-1 cursor-pointer text-orange-600 hover:bg-gray-100"
              onClick={() => {
                handleDeleteSeries(contextMenu.event.seriesId);
              }}
            >
              Delete Entire Series
            </li>
          )}
          <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100"
            onClick={() => {
              setEditingEvent(enrichEventWithAccount(contextMenu.event));
              setShowModal(true);
              setContextMenu({ visible: false, x: 0, y: 0, event: null });
            }}
          >
            {(contextMenu.event?.timecodeName === '(SYNC) Outlook Sync' || String(contextMenu.event?.id || contextMenu.event?.logId || '').startsWith('crm:') || String(contextMenu.event?.id || contextMenu.event?.logId || '').startsWith('projects:crm:')) ? 'Convert' : 'Edit'}
          </li>
          <li
            className="px-2 py-1 cursor-pointer text-red-600 hover:bg-gray-100"
            onClick={() => {
              setEventPendingDelete(contextMenu.event);
              setShowDeleteConfirm(true);
              setContextMenu({ visible: false, x: 0, y: 0, event: null });
            }}
          >
            Delete
          </li>
        </ul>
      )}

      {/* Context Menu for Empty Slots */}
      {slotContextMenu.visible && appView !== 'calendar' && (
        <ul
          ref={slotContextMenuRef}
          className="absolute bg-white border border-gray-200 shadow-lg rounded-md p-2 z-50"
          style={{
            top: slotContextMenu.y,
            left: slotContextMenu.x,
          }}
        >
          <li
            className="px-2 py-1 cursor-pointer hover:bg-gray-100"
            onClick={() => {
              const slotInfo = slotContextMenu.slot;
              const placeholder = createMarkedPlaceholder(slotInfo.start, slotInfo.end);
              setEvents(prev => [
                // fix: startswith -> startsWith
                ...prev.filter(ev => ev.approval_status !== 'Marked' && !(ev.logId && ev.logId.startsWith('temp-'))),
                placeholder
              ]);
              setSlotInfo({ start: slotInfo.start, end: slotInfo.end, tempId: placeholder.logId });
              setModalPosition(getModalPositionStyle(slotInfo.start, currentView));
              setShowModal(true);
              setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
            }}
          >
            Add New
          </li>
          {eventToCopy && (
            <li
              className="px-2 py-1 cursor-pointer hover:bg-gray-100"
              onClick={() => {
                // Paste copied event at this slot
                const slotInfo = slotContextMenu.slot;
                const duration = moment(eventToCopy.end).diff(moment(eventToCopy.start));
                const newEnd = moment(slotInfo.start).add(duration, 'ms').toDate();
                const copiedEvent = {
                  ...eventToCopy,
                  start: slotInfo.start,
                  end: newEnd,
                };
                
                handleSaveEvent(copiedEvent);
                setEventToCopy(null);
                setSlotContextMenu({ visible: false, x: 0, y: 0, slot: null });
              }}
            >
              Paste
            </li>
          )}
        </ul>
      )}

      {!isDragging && (
        <EventFormModal
        onTimeChange={handleModalTimeChange}
        onNavigate={handleNavigate}
        currentDate={currentDate}
        key={editingEvent?.logId || slotInfo?.tempId || 'new'}
        modalPosition={modalPosition}
        setModalPosition={setModalPosition}
        getModalPositionStyle={getModalPositionStyle}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingEvent(null);
          if (slotInfo?.tempId) {
            setEvents(prev => prev.filter(ev => ev.logId !== slotInfo.tempId));
          }
          setSlotInfo(null);
          // Only restore if we didn't just save/update
          if (!suppressRevertRef.current && originalEventBeforeEdit) {
            setEvents(prev =>
              prev.map(ev =>
                ev.logId === originalEventBeforeEdit.logId ? originalEventBeforeEdit : ev
              )
            );
          }
          setOriginalEventBeforeEdit(null);
          suppressRevertRef.current = false; // reset flag
        }}
        onSave={async (eventData) => {
          if (eventData.sendToHR) {
            if (editingEvent?.isHR) {
              setEvents(prev => prev.map(ev => ev.logId === editingEvent.logId
                ? { ...ev, title: eventData.title || 'Arbejdstid til HR', start: eventData.start, end: eventData.end }
                : ev
              ));
            } else {
              setEvents(prev => [...prev, {
                title: eventData.title || 'Arbejdstid til HR',
                start: eventData.start,
                end: eventData.end,
                logId: `hr:${Date.now()}`,
                isHR: true,
              }]);
            }
          } else if (eventData.isWorkdaySaved) {
            showSuccess('Arbejdstid gemt', 'Din arbejdstid er registreret.');
            const empEmail = selectedEmployee?.email || null;
            API.refreshPeopleTimelogs(empEmail).then(peopleResp => {
              const peopleLogs = peopleResp.data?.flatMap(g => g.timelogs || []) || [];
              const freshPeopleEvents = peopleLogs.map(log => handlePeopleTimelogToEvent(log)).filter(Boolean);
              setEvents(prev => [...prev.filter(ev => !ev.isPeople), ...freshPeopleEvents]);
            }).catch(() => {});
          } else if (eventData.isPeopleLeave) {
            await handleSaveLeave(eventData);
          // Only for editing, not new
         } else if (eventData.convertFromCRM) {
          await handleConvertCRMSync(eventData);
        } else if (
          editingEvent &&
          (editingEvent.timecodeName === '(SYNC) Outlook Sync' || String(editingEvent.id || editingEvent.logId || '').startsWith('crm:') || String(editingEvent.id || editingEvent.logId || '').startsWith('projects:crm:')) &&
          eventData.projectId !== editingEvent.projectId
        ) {
          // Delete old, then create new
          await handleDeleteEvent(editingEvent.logId, editingEvent.projectId, editingEvent.timecodeId);
          handleSaveEvent(eventData);
        } else if (editingEvent) {
          handleUpdateEvent({ ...eventData, logId: editingEvent.logId });
          if (featureFlags.enableRolle) {
            const prevTaskId = editingEvent.taskId || null;
            const newTaskId = eventData.taskId || null;
            if (prevTaskId !== newTaskId) {
              if (prevTaskId) API.removeRolle(editingEvent.projectId || eventData.projectId, prevTaskId).catch(() => {});
              if (newTaskId) API.assignRolle(eventData.projectId, newTaskId).catch(() => {});
            }
          }
        } else {
          handleSaveEvent(eventData);
        }
        setShowModal(false);
        setEditingEvent(null);
        setSlotInfo(null);
        setOriginalEventBeforeEdit(null);
        }}
        onDelete={handleDeleteEvent}
        start={editingEvent?.start || slotInfo?.start}
        end={editingEvent?.end || slotInfo?.end}
        projects={projects}
        initialData={editingEvent}
        selectedSlotDate={selectedSlotDate}
        currentView={currentView}
        billableAutoFill={billableAutoFill}
        enableLeave={featureFlags.enableLeave}
        enablePeople={featureFlags.enablePeople}
        directProjectMode={featureFlags.directProjectMode}
        filterProjectsByAssignment={featureFlags.filterProjectsByAssignment}
        preloadedAssignedProjects={preloadedAssignedProjects}
        isAdmin={featureFlags.isAdmin}
      />
      )}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onConfirm={() => {
          if (eventPendingDelete) {
            handleDeleteEvent(eventPendingDelete.logId, eventPendingDelete.projectId, eventPendingDelete.timecodeId, eventPendingDelete.isPeople, eventPendingDelete.taskId);
            setEventPendingDelete(null);
            setShowDeleteConfirm(false);
          }
        }}
        onCancel={() => {
          setEventPendingDelete(null);
          setShowDeleteConfirm(false);
        }}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      
      {/* Series Delete Confirmation Modal */}
      <ConfirmModal 
        isOpen={showSeriesDeleteConfirm}
        onConfirm={handleConfirmDeleteSeries}
        onCancel={() => {
          setSeriesPendingDelete({ seriesId: null, events: [] });
          setShowSeriesDeleteConfirm(false);
        }}
        title="Delete Entire Series"
        message={`Are you sure you want to delete ${seriesPendingDelete.events.length} time registration${seriesPendingDelete.events.length !== 1 ? 's' : ''} from this series? This action cannot be undone.`}
        confirmText={`Delete ${seriesPendingDelete.events.length} Registration${seriesPendingDelete.events.length !== 1 ? 's' : ''}`}
        confirmButtonClass="bg-orange-600 hover:bg-orange-700"
      />
      
      {/* Series Deletion Progress Overlay */}
      {isDeletingSeries && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="flex items-center justify-center mb-4">
                <div 
                  className="animate-spin rounded-full h-8 w-8 border-b-2 mr-3"
                  style={{ borderColor: '#ccb778' }}
                ></div>
                <div className="text-lg font-semibold text-gray-900">
                  Deleting Series
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-4">
                Deleting {deleteProgress.current} of {deleteProgress.total} registrations...
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="h-3 rounded-full transition-all duration-300"
                  style={{ 
                    width: `${(deleteProgress.current / deleteProgress.total) * 100}%`,
                    backgroundColor: '#ccb778'
                  }}
                ></div>
              </div>
              <div className="text-xs text-gray-500 mt-2">
                Please wait while we delete all entries...
              </div>
            </div>
          </div>
        </div>
      )}
      
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title={errorModalData.title}
        message={errorModalData.message}
        variant={errorModalData.variant}
      />
      
      {/* Remove Outlook Syncs Confirmation Modal */}
      <ConfirmModal 
        isOpen={showRemoveSyncsConfirm}
        onConfirm={handleConfirmRemoveSyncs}
        onCancel={handleCancelRemoveSyncs}
        title="Remove Outlook Syncs"
        message={`This will delete ${outlookSyncsToRemove.length} Outlook sync event${outlookSyncsToRemove.length !== 1 ? 's' : ''} from the current view. This action cannot be undone. Continue?`}
        confirmText="Remove"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />

      {/* Remove Outlook Syncs Result Modal */}
      {showRemoveSyncsResult && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-[2700] flex items-center justify-center p-3 md:p-4">
          <div className="bg-white p-3 md:p-6 rounded-lg w-full max-w-[88vw] md:max-w-[400px] shadow-xl">
            <div className="flex justify-end">
              <button
                onClick={() => setShowRemoveSyncsResult(false)}
                className="px-4 py-2.5 md:py-2 text-sm md:text-base bg-[#ccb778] text-white rounded hover:bg-[#b49646] touch-manipulation min-h-[44px] md:min-h-0"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* 🛡️ Series Recovery Modal */}
      {showRecoveryModal && recoveryData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="text-center">
              <div className="text-lg font-semibold text-gray-900 mb-4">
                📋 Incomplete Series Found
              </div>
              <div className="text-sm text-gray-600 mb-4">
                Found an incomplete series creation from {recoveryData.hoursElapsed < 1 ? 
                  `${Math.round(recoveryData.hoursElapsed * 60)} minutes` : 
                  `${recoveryData.hoursElapsed} hours`} ago:
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-4">
                <div className="text-sm">
                  <strong>Series:</strong> {recoveryData.templateData.title}<br/>
                  <strong>Created:</strong> {recoveryData.createdEvents.length} of {recoveryData.totalEvents} events<br/>
                  <strong>Project:</strong> {projects.find(p => p.id === recoveryData.templateData.projectId)?.name || 'Unknown'}
                </div>
              </div>
              <div className="text-sm text-gray-600 mb-6">
                What would you like to do with the already created time registrations?
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleRecoveryCleanup}
                  disabled={isRecovering}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 text-sm"
                >
                  {isRecovering ? 'Deleting...' : '🗑️ Delete All'}
                </button>
                <button
                  onClick={handleRecoveryDismiss}
                  disabled={isRecovering}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 text-sm"
                >
                  ✅ Keep Events
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Series Modal */}
      {featureFlags.enableSeries && (
        <SeriesModal
          isOpen={showSeriesModal}
          onClose={() => {
            setShowSeriesModal(false);
            setSeriesTemplateEvent(null);
          }}
          onCreateSeries={handleBatchCreateSeries}
          templateEvent={seriesTemplateEvent}
          projects={projects}
        />
      )}

      {/* Custom Date Range Modal */}
      <CustomDateRangeModal
        isOpen={showCustomDateModal}
        onClose={handleCustomDateRangeClose}
        onApply={handleCustomDateRangeApply}
        currentRange={customDateRange}
        isPeopleContext={customRangeContext === 'people'} // NEW
      />
    </div>
  );
}

export default App;